<script setup lang="ts">
import { capitalize, startCase } from 'lodash'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { TheHeader, VButton, VDropdownImproved, VIcon } from '@/modules/shared/components'
import { useEntityStore } from '@/modules/investing/stores/better-entity-store'
import { useAuthStore } from '../../auth/stores/auth-store'
import { rails_url } from '@/modules/shared/utils/rails'

///////////////////////////////////////////////////////////////////////////////
// Utils
///////////////////////////////////////////////////////////////////////////////

function formattedEntityType(entity_type: string) {
  switch (entity_type) {
    case 'fund':
      return capitalize(t('shared.fund'))
    case 'gp':
      return `${startCase(t('shared.general partner'))} (${t('shared.GP')})`
    case 'spv':
      return `${startCase(t('shared.special purpose vehicle'))} (${t('shared.SPV')})`
    default:
      return null
  }
}

function backButtonLabel(entity_type: string) {
  switch (entity_type) {
    case 'fund':
      return `${capitalize(t('shared.fund', 0))}`
    case 'gp':
      return `${t('shared.GP', 0)}`
    case 'spv':
      return `${t('shared.SPV', 0)}`
    default:
      return null
  }
}

///////////////////////////////////////////////////////////////////////////////
const { t } = useI18n()
const route = useRoute()
const authStore = useAuthStore()
const entityStore = useEntityStore()

const entity = computed(() => entityStore.entity)

const back_url_config = computed(() => {
  if (!entity.value) return null

  if (authStore.is_company_user) {
    return {
      to: `${rails_url()}/${entity.value?.entity_type}s`,
      fallback_url: `${rails_url()}/${entity.value?.entity_type}s`,
      label: `← Back`,
    }
  } else if (!authStore.is_site_or_group_admin) {
    return {
      to: {
        name: 'dashboard',
      },
      fallback_url: {
        name: 'investing.entity-overview',
        params: { slug: route.params.slug, entity_type: entity.value?.entity_type, entity_id: entity.value?.id },
      },
      label: `← Back`,
    }
  } else {
    return {
      to: {
        name: 'investing.entities',
        params: {
          entity_type: `${entity.value?.entity_type}s`,
          slug: route.params.slug,
        },
      },
      fallback_url: {
        name: 'investing.entity-overview',
        params: { slug: route.params.slug, entity_type: entity.value?.entity_type, entity_id: entity.value?.id },
      },
      label: `← Back to all ${backButtonLabel(entity.value?.entity_type as string)}`,
    }
  }
})
</script>

<template>
  <TheHeader
    :descriptor="formattedEntityType(entity?.entity_type)"
    :subtitle="entity?.legal_name"
    :title="entity?.name"
    :back_url_config="back_url_config"
  >
    <template #actions>
      <div class="flex items-center justify-end space-x-2">
        <slot name="actions"></slot>
      </div>
    </template>
  </TheHeader>
</template>
